// P11 — Visualização do rito para o comitê (board por estágio + pendências).
//
// FUNÇÃO PURA — zero infra, zero decifragem. Recebe os DenunciaRecord crus e
// devolve, por caso NÃO-encerrado, SÓ metadados content-free: caseId, categoria,
// estado (state.kind), âncoras de prazo (timestamps de processo, nunca do
// denunciante) e uma lista de PENDÊNCIAS derivadas do estado — o que falta para
// avançar. Nenhum ciphertext é aberto, nenhum teor/parecer é lido: a única coisa
// que a função extrai de `pareceres`/`oitivas` são CONTAGENS (voto cego via
// quorumProgressV2, oitiva sem ata via status). Anonimização por construção
// (padrão D-212 de indicadores/composicao): a saída não identifica denunciante
// nem revela QUEM votou/o TEOR.

import type { DenunciaCategoria } from './categoria.js';
import type { DenunciaRecord } from './denuncia.js';
import type { DenunciaState } from './state-machine.js';
import { quorumProgressV2 } from './state-machine.js';
import type { OitivaOuDispensa } from './oitiva.js';

/** Colunas do board, na ordem canônica do rito. `Encerrada` fica de fora
 *  (board é só de casos EM CURSO). AguardandoRetornoComite (reabertura) cai em
 *  Avaliação; AguardandoDecisao cai em Decisão (quórum fechado, aguardando
 *  emit_decisao). */
export const RITO_STAGES = [
  'Aberta',
  'Triagem',
  'Instrucao',
  'Oitivas',
  'Avaliacao',
  'Decisao',
  'Recursal',
] as const;
export type RitoStage = (typeof RITO_STAGES)[number];

export const RITO_STAGE_LABEL: Readonly<Record<RitoStage, string>> = {
  Aberta: 'Aberta',
  Triagem: 'Triagem',
  Instrucao: 'Instrução',
  Oitivas: 'Oitivas',
  Avaliacao: 'Avaliação',
  Decisao: 'Decisão',
  Recursal: 'Recursal',
};

export type PendenciaCode =
  | 'triagem_pendente'
  | 'triagem_nao_classificada'
  | 'atribuicao_pendente'
  | 'oitivas_nao_iniciadas'
  | 'oitiva_pendente_registro'
  | 'oitiva_sem_ata'
  | 'oitivas_prontas_fechar'
  | 'quorum_incompleto'
  | 'quorum_fechado_aguarda_decisao'
  | 'decisao_pendente'
  | 'retorno_comite_pendente'
  | 'aguardando_prazo_recursal'
  | 'recurso_em_prazo';

export interface RitoPendencia {
  readonly code: PendenciaCode;
  /** Rótulo pt-BR pronto para exibição (content-free). */
  readonly label: string;
  /** Detalhe agregado opcional — ex.: "2/3" (quórum). Nunca identifica ninguém. */
  readonly detail?: string;
}

export interface RitoCaseCard {
  readonly caseId: string;
  readonly categoria: DenunciaCategoria;
  /** state.kind cru (para agrupamento fino / debug). */
  readonly estado: DenunciaState['kind'];
  readonly stage: RitoStage;
  /** Âncoras de prazo (metadado de processo do comitê, não do denunciante). */
  readonly openedAt: number | null;
  readonly classifiedAt: number | null;
  /** Fim do prazo recursal (só no estado Recursal). */
  readonly prazoRecursalFimEpochMs: number | null;
  readonly pendencias: ReadonlyArray<RitoPendencia>;
}

export interface RitoStageColumn {
  readonly stage: RitoStage;
  readonly label: string;
  readonly cases: ReadonlyArray<RitoCaseCard>;
}

export interface RitoBoard {
  readonly stages: ReadonlyArray<RitoStageColumn>;
  /** Total de casos em curso (não-encerrados) no board. */
  readonly total: number;
}

/**
 * Estados em que o caso está SOB APURAÇÃO — investigação instaurada e ainda não
 * decidida.
 *
 * Promovido do `CaseDetailPage` (onde se chamava `ESTADOS_INVESTIGACAO_PLANO` e
 * governava a aba Plano) para o core em 2026-08-13, quando o dashboard passou a
 * filtrar por "em apuração". Duas cópias divergiriam no primeiro estado novo, e a
 * divergência apareceria como caso que o filtro esconde mas a aba Plano oferece.
 *
 * ⚠️ NOMENCLATURA: o `IT-CMP-002` chama de "Fase 5 — Apuração" apenas a etapa de
 * coleta e oitivas. Este conjunto é MAIS LARGO — cobre da abertura da investigação
 * até a decisão pendente — porque a pergunta do filtro é "quais casos estão em
 * andamento?", não "quais estão na Fase 5". Nome herdado do uso, não do documento.
 */
export const ESTADOS_EM_APURACAO: ReadonlySet<DenunciaState['kind']> = new Set([
  'Instrucao',
  'Oitivas',
  'AvaliacaoEmCurso',
  'AguardandoDecisao',
]);

/** state.kind → coluna do board. `Encerrada` → null (fora do board).
 *
 *  EXPORTADO em 2026-08-13 para o filtro/ordenação do dashboard. Era privado, e a
 *  alternativa seria a lista do dashboard redigitar o mapa kind→fase — segunda redação
 *  de uma regra que já tem autoridade aqui, junto de `RITO_STAGES` (a ordem canônica).
 *  Duas cópias divergiriam no primeiro estado novo do rito. */
export function stageOf(kind: DenunciaState['kind']): RitoStage | null {
  switch (kind) {
    case 'Aberta':
      return 'Aberta';
    case 'Triagem':
      return 'Triagem';
    case 'Instrucao':
      return 'Instrucao';
    case 'Oitivas':
      return 'Oitivas';
    case 'AvaliacaoEmCurso':
    case 'AguardandoRetornoComite':
      return 'Avaliacao';
    case 'AguardandoDecisao':
    case 'Decisao':
      return 'Decisao';
    case 'Recursal':
      return 'Recursal';
    case 'Encerrada':
      return null;
    default:
      return null;
  }
}

function p(code: PendenciaCode, label: string, detail?: string): RitoPendencia {
  return detail === undefined ? { code, label } : { code, label, detail };
}

/**
 * Pendências do caso derivadas SÓ do estado (o que falta para avançar). Content-
 * free: a única leitura de `pareceres`/`oitivas` é CONTAGEM (voto cego).
 */
function pendenciasOf(
  state: DenunciaState,
  rosterExigivel: ReadonlyArray<string>,
): RitoPendencia[] {
  switch (state.kind) {
    case 'Aberta':
      return [p('triagem_pendente', 'Aguardando abertura da triagem')];
    case 'Triagem':
      // Sem classificação ainda? senão classificada, aguardando designação de membros.
      return state.triagem === undefined
        ? [p('triagem_nao_classificada', 'Triagem não classificada')]
        : [p('atribuicao_pendente', 'Aguardando designação de membros')];
    case 'Instrucao':
      return [p('oitivas_nao_iniciadas', 'Aguardando início das oitivas')];
    case 'Oitivas': {
      const oitivas: ReadonlyArray<OitivaOuDispensa> = state.oitivas;
      if (oitivas.length === 0) {
        return [p('oitiva_pendente_registro', 'Nenhuma oitiva/dispensa registrada')];
      }
      const semAta = oitivas.filter((o) => o.status === 'pendente_anexo').length;
      if (semAta > 0) {
        return [p('oitiva_sem_ata', 'Oitiva(s) sem ata anexada', `${semAta}`)];
      }
      return [p('oitivas_prontas_fechar', 'Oitivas prontas — aguardando fechamento')];
    }
    case 'AvaliacaoEmCurso': {
      // 🔴 `rosterExigivel`, NÃO `state.assignedMembers` (corrigido 2026-08-17).
      //
      // Este era o 7º sítio a responder "quem o rito espera" e o único que não passava
      // por `ehMembroExigivel`: contava TODO designado, incluindo a cadeira `juridico`
      // (não-bloqueante desde 16/08) e o `admin_master` que não assumiu o caso. O
      // sintoma foi "Quórum de pareceres incompleto (0/6)" num colegiado de 3.
      //
      // A regra não pode ser calculada aqui — depende do UsersRepo e da visibilidade
      // do caso, e este módulo é PURO. Por isso o roster ENTRA por parâmetro, e entra
      // como OBRIGATÓRIO: com valor default, o chamador que esquecesse voltaria a
      // contar errado em silêncio, que é exatamente como este bug nasceu.
      const { voted, total } = quorumProgressV2(rosterExigivel, state.pareceres);
      return voted < total
        ? [p('quorum_incompleto', 'Quórum de pareceres incompleto', `${voted}/${total}`)]
        : [
            p(
              'quorum_fechado_aguarda_decisao',
              'Quórum completo — aguardando fechamento',
              `${voted}/${total}`,
            ),
          ];
    }
    case 'AguardandoRetornoComite':
      return [p('retorno_comite_pendente', 'Comitê reabriu — aguardando definição de retorno')];
    case 'AguardandoDecisao':
      return [p('decisao_pendente', 'Quórum fechado — aguardando decisão')];
    case 'Decisao':
      return [p('aguardando_prazo_recursal', 'Decisão emitida — aguardando prazo recursal')];
    case 'Recursal':
      return [p('recurso_em_prazo', 'Em prazo recursal')];
    case 'Encerrada':
      return [];
    default:
      return [];
  }
}

/**
 * Deriva o card content-free de um caso. Retorna null para casos encerrados
 * (fora do board) ou estados sem coluna.
 */
export function deriveRitoPendencias(
  record: DenunciaRecord,
  /** Quem o rito espera NESTE caso (`exigiveisDoCaso` no api). Obrigatório de
   *  propósito — ver o comentário do quórum em `pendenciasOf`. */
  rosterExigivel: ReadonlyArray<string>,
): RitoCaseCard | null {
  const state = record.state as DenunciaState;
  const stage = stageOf(state.kind);
  if (stage === null) return null;

  const openedAt = 'openedAt' in state ? state.openedAt : null;
  const classifiedAt = 'triagem' in state && state.triagem ? state.triagem.classifiedAt : null;
  const prazoRecursalFimEpochMs = state.kind === 'Recursal' ? state.prazoFimEpochMs : null;

  return {
    caseId: record.caseId,
    categoria: record.categoria,
    estado: state.kind,
    stage,
    openedAt,
    classifiedAt,
    prazoRecursalFimEpochMs,
    pendencias: pendenciasOf(state, rosterExigivel),
  };
}

/**
 * Monta o board completo: casos NÃO-encerrados agrupados por estágio, na ordem
 * canônica do rito. Função PURA e determinística (sem I/O, sem Date.now).
 */
export function deriveRitoBoard(
  records: ReadonlyArray<DenunciaRecord>,
  /** `caseId → roster exigível`. Um mapa e não uma função: o chamador percorre dezenas
   *  de casos e precisa ler usuários + visibilidade UMA vez (`exigiveisDoCasoCom`).
   *  Caso ausente do mapa cai em roster VAZIO — o quórum aparece como `0/0`, que é
   *  visivelmente errado, e não como um número plausível vindo do roster errado. */
  rosterPorCaso: ReadonlyMap<string, ReadonlyArray<string>>,
): RitoBoard {
  const buckets: Record<RitoStage, RitoCaseCard[]> = {
    Aberta: [],
    Triagem: [],
    Instrucao: [],
    Oitivas: [],
    Avaliacao: [],
    Decisao: [],
    Recursal: [],
  };
  let total = 0;
  for (const r of records) {
    const card = deriveRitoPendencias(r, rosterPorCaso.get(r.caseId) ?? []);
    if (card === null) continue;
    buckets[card.stage].push(card);
    total += 1;
  }
  const stages: RitoStageColumn[] = RITO_STAGES.map((stage) => ({
    stage,
    label: RITO_STAGE_LABEL[stage],
    cases: buckets[stage],
  }));
  return { stages, total };
}
